/**
 * The furniture every section sits in: the mark, the header, the footer, and
 * the wrapper that gives a section its width and its eyebrow.
 *
 * These are local to this app rather than in `@wend/ui` for the reason
 * `index.css` gives — the design system is a dense application UI, and a page
 * that is read once at arm's length needs fewer, larger things. Nothing here is
 * a fork of a component that already exists; there is no `Section` or `Mark` in
 * `@wend/ui` to fork.
 *
 * ONE COLOUR RULE, INHERITED. `--color-faint` is not a text colour (theme.css
 * says so at length, and a probe in the product counts its uses). Small text on
 * this page is `text-muted`, whose contrast clears 4.5:1 on both surfaces here.
 */
import type { ReactNode } from 'react';

import { cn } from '@wend/ui';

import { WendMarkAnimated } from './WendMarkAnimated';

/**
 * The mark, from `public/favicon.svg`, inlined so it can take a size and a
 * colour from the page rather than being a fixed image.
 *
 * It draws what the product is named after: a path that sets off, turns, and
 * arrives somewhere. The gradient is on the ring at the end — the arrival —
 * which is the one place the brand gradient appears in the mark.
 */
export function Mark({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 220 220" aria-hidden="true" className={cn('size-8', className)}>
      <defs>
        <linearGradient id="wend-mark-ring" x1="99" y1="51" x2="141" y2="9" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#6D5EF6" />
          <stop offset="1" stopColor="#9B5EF6" />
        </linearGradient>
      </defs>
      <rect width="220" height="220" rx="48" fill="#2A2150" />
      <g transform="translate(7.4,8.6) scale(1.2)">
        <circle cx="40" cy="150" r="10" fill="#FFFFFF" />
        <path
          d="M40 150 L40 96 Q40 84 52 84 L108 84 Q120 84 120 72 L120 45"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <circle cx="120" cy="30" r="15" fill="none" stroke="url(#wend-mark-ring)" strokeWidth="12" />
      </g>
    </svg>
  );
}

/** The wordmark: the mark (drawing itself once, on load) and the name. Header and footer. */
export function Wordmark({ className, animate = false }: { readonly className?: string; readonly animate?: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      {animate ? <WendMarkAnimated size={28} variant="once" className="shadow-none" /> : <Mark className="size-7" />}
      <span className="text-[1.05rem] font-semibold tracking-tight text-text">Wend</span>
    </span>
  );
}

const NAV = [
  { href: '#reach', label: 'Reach' },
  { href: '#connect', label: 'Connect' },
  { href: '#sense', label: 'Sense' },
  { href: '#flow', label: 'Flow' },
  { href: '#loop', label: 'Try it' },
] as const;

const navLink =
  'whitespace-nowrap rounded-base px-2.5 py-2 text-[0.9rem] text-muted transition-colors hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a';

/**
 * The header: the wordmark, the four parts and "Try it" as anchors into this
 * one page, "Sign in" for a customer, and "Try Wend" — the page's one ask.
 * Below `md` the anchors leave (the page is a single scroll on a phone, and
 * five links do not fit beside the ask at 375px); "Sign in" and "Try Wend" stay.
 */
export function SiteHeader() {
  return (
    <header className="sticky top-0 z-20 border-b border-line bg-ink/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-8">
        <a href="#top" className="rounded-base focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-a">
          <Wordmark animate />
          <span className="sr-only">Wend, top of page</span>
        </a>
        <nav aria-label="Sections" className="hidden items-center gap-0.5 md:flex">
          {NAV.map((item) => (
            <a key={item.href} href={item.href} className={navLink}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-1 sm:gap-2">
          <a href="https://app.wendhq.com" className={navLink}>
            Sign in
          </a>
          <a
            href="#talk"
            className="whitespace-nowrap rounded-base bg-brand-a px-4 py-2 text-[0.9rem] font-medium text-white transition-colors hover:bg-brand-b focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
          >
            Try Wend
          </a>
        </div>
      </div>
    </header>
  );
}

/** The small mono label above a section's heading. The app uses the same treatment. */
export function Eyebrow({ children }: { readonly children: ReactNode }) {
  return (
    <p className="font-mono text-[0.72rem] uppercase tracking-[0.18em] text-muted">{children}</p>
  );
}

/** A section: one width, one rhythm, an optional id for the header's anchors. */
export function Section({
  id,
  className,
  children,
}: {
  readonly id?: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  return (
    <section id={id} className={cn('scroll-mt-20 border-t border-line px-5 py-20 sm:px-8 sm:py-28', className)}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

/** The footer: what Wend is in one line, how to reach a person, and where the product is. */
export function SiteFooter({ contactEmail }: { readonly contactEmail: string }) {
  return (
    <footer className="border-t border-line px-5 py-12 sm:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <Wordmark />
          <p className="text-[0.85rem] text-muted">Reach · Connect · Sense · Flow. On your own accounts.</p>
        </div>
        <div className="flex flex-col gap-1.5 text-[0.85rem] sm:items-end">
          <a
            href={`mailto:${contactEmail}`}
            className="rounded-base text-muted transition-colors hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
          >
            {contactEmail}
          </a>
          <a
            href="https://app.wendhq.com"
            className="rounded-base text-muted transition-colors hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-a"
          >
            app.wendhq.com
          </a>
        </div>
      </div>
    </footer>
  );
}
